import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { ChevronDown, ChevronUp, Lock, ShieldCheck, Video } from 'lucide-react';
import { Card, Button } from '../components';
import { getProfessionals, startTraject } from '../services/medewerkerApi';
import type { DemografieOpgave, PortaalProfessional, PoolStatus } from '../services/medewerkerApi';

function foutMelding(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
  }
  return fallback;
}

function initialen(naam: string): string {
  return naam
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

const STATUS_DOT: Record<PoolStatus, string> = {
  GROEN: 'bg-green-500',
  BLAUW: 'bg-blue-500',
  ORANJE: 'bg-amber-400',
  ROOD: 'bg-red-500',
};

const Chip: React.FC<{ children: React.ReactNode; kleur?: 'blauw' | 'groen' | 'paars' | 'grijs' }> = ({ children, kleur = 'grijs' }) => {
  const kleuren = {
    blauw: 'bg-blue-50 text-[#0B1F3A] border-blue-100',
    groen: 'bg-green-50 text-green-800 border-green-100',
    paars: 'bg-purple-50 text-purple-800 border-purple-100',
    grijs: 'bg-gray-100 text-gray-700 border-gray-200',
  };
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${kleuren[kleur]}`}>{children}</span>;
};

/** Disciplines waarvan het jaarverslag een (anonieme) demografische verdeling toont. */
const DEMOGRAFIE_TYPES = ['vertrouwenspersoon', 'bedrijfsmaatschappelijk_werker'];

const DemografieVraag: React.FC<{ bezig: boolean; onStart: (o: DemografieOpgave | null) => void; onAnnuleer: () => void }> = ({ bezig, onStart, onAnnuleer }) => {
  const { t } = useTranslation('medewerker');
  const [o, setO] = useState<DemografieOpgave>({ geslacht: null, leeftijdsband: null });
  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
      <p className="text-sm text-gray-800">
        {t('demografie.uitleg', {
          defaultValue:
            'Optioneel: help mee aan het anonieme jaarverslag. Je antwoord wordt los van je gesprek bewaard; niemand kan het aan jou of je melding koppelen.',
        })}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <label className="block text-xs text-gray-700">
          {t('demografie.geslacht', { defaultValue: 'Geslacht' })}
          <select className="mt-1 w-full rounded-md border-gray-300 text-sm" value={o.geslacht ?? ''} onChange={(e) => setO({ ...o, geslacht: (e.target.value || null) as DemografieOpgave['geslacht'] })}>
            <option value="">{t('demografie.liever', { defaultValue: 'Zeg ik liever niet' })}</option>
            <option value="vrouw">{t('demografie.vrouw', { defaultValue: 'Vrouw' })}</option>
            <option value="man">{t('demografie.man', { defaultValue: 'Man' })}</option>
            <option value="non_binair">{t('demografie.nonBinair', { defaultValue: 'Non-binair' })}</option>
          </select>
        </label>
        <label className="block text-xs text-gray-700">
          {t('demografie.leeftijd', { defaultValue: 'Leeftijd' })}
          <select className="mt-1 w-full rounded-md border-gray-300 text-sm" value={o.leeftijdsband ?? ''} onChange={(e) => setO({ ...o, leeftijdsband: (e.target.value || null) as DemografieOpgave['leeftijdsband'] })}>
            <option value="">{t('demografie.liever', { defaultValue: 'Zeg ik liever niet' })}</option>
            <option value="lt25">&lt; 25</option>
            <option value="25_40">25 – 40</option>
            <option value="41_55">41 – 55</option>
            <option value="gt55">&gt; 55</option>
          </select>
        </label>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={onAnnuleer} disabled={bezig}>
          {t('algemeen.annuleren', { defaultValue: 'Annuleren' })}
        </Button>
        <Button className="flex-1" onClick={() => onStart(o.geslacht || o.leeftijdsband ? o : null)} loading={bezig} disabled={bezig}>
          {t('demografie.start', { defaultValue: 'Start traject' })}
        </Button>
      </div>
    </div>
  );
};

interface KaartProps {
  p: PortaalProfessional;
  open: boolean;
  onToggle: () => void;
  bezig: boolean;
  fout: string | null;
  onStart: (demografie?: DemografieOpgave | null) => void;
}

const ProfessionalKaart: React.FC<KaartProps> = ({ p, open, onToggle, bezig, fout, onStart }) => {
  const { t } = useTranslation('medewerker');
  const navigate = useNavigate();

  const statusLabels: Record<PoolStatus, string> = {
    GROEN: t('status.groen', { defaultValue: 'Direct beschikbaar' }),
    BLAUW: t('status.blauw', { defaultValue: 'Beschikbaar' }),
    ORANJE: t('status.oranje', { defaultValue: 'Beperkt' }),
    ROOD: t('status.rood', { defaultValue: 'Agenda vol' }),
  };

  const max = p.sessies_max;
  const pct = max && max > 0 ? Math.min(100, Math.round((p.sessies_gebruikt / max) * 100)) : 0;

  let knopTekst = t('professionals.startTraject', { defaultValue: 'Start traject (60 min)' });
  const [vraagOpen, setVraagOpen] = useState(false);
  const vraagDemografie = DEMOGRAFIE_TYPES.includes(p.professional_type);
  let knopActie: () => void = vraagDemografie ? () => setVraagOpen(true) : () => onStart();
  if (p.lopend_traject?.soort === 'dossier' && p.lopend_traject.booking_token) {
    const token = p.lopend_traject.booking_token;
    knopTekst = t('professionals.afspraakPlannen', { defaultValue: 'Afspraak plannen' });
    knopActie = () => navigate(`/plannen/${token}`);
  } else if (p.lopend_traject?.soort === 'vp') {
    const id = p.lopend_traject.id;
    knopTekst = t('professionals.gesprekPlannen', { defaultValue: 'Gesprek plannen' });
    knopActie = () => navigate(`/medewerker/vp/${id}`);
  }
  const heeftLopend = Boolean(p.lopend_traject);
  const geblokkeerd = p.plafond_bereikt && !heeftLopend;
  const detailId = `prof-detail-${p.professional_id}`;

  return (
    <Card className="p-4">
      <div className="flex items-start gap-3">
        {p.foto_url ? (
          <img src={p.foto_url} alt="" className="w-14 h-14 rounded-full object-cover shrink-0 bg-gray-100" />
        ) : (
          <span className="w-14 h-14 rounded-full bg-blue-50 text-[#0B1F3A] font-semibold flex items-center justify-center shrink-0" aria-hidden="true">
            {initialen(p.naam)}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 leading-tight">{p.naam}</p>
          <p className="text-xs text-gray-500 mt-0.5">{p.discipline_label}</p>
          <p className="text-xs text-gray-700 mt-1 flex items-center gap-1.5">
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${STATUS_DOT[p.pool_status]}`} aria-hidden="true" />
            {statusLabels[p.pool_status]}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mt-3">
        {p.alleen_online && <Chip kleur="blauw">{t('professionals.alleenOnline', { defaultValue: '100% online' })}</Chip>}
        {p.is_vertrouwenspersoon && (
          <Chip kleur="paars">
            <ShieldCheck className="w-3 h-3" aria-hidden="true" />
            {t('professionals.vpChip', { defaultValue: 'Vertrouwenspersoon — geen verslagen, chat wist na 24u' })}
          </Chip>
        )}
        {p.is_arbo && <Chip kleur="groen">{t('professionals.arboChip', { defaultValue: 'Arbo / Poortwachter' })}</Chip>}
      </div>

      {max !== null && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs text-gray-600">
            <span>{t('professionals.sessies', { defaultValue: 'Sessies: {{gebruikt}}/{{max}}', gebruikt: p.sessies_gebruikt, max })}</span>
            {p.plafond_bereikt && (
              <span className="text-red-700 font-medium flex items-center gap-1">
                <Lock className="w-3 h-3" aria-hidden="true" />
                {t('professionals.maxBereikt', { defaultValue: 'Maximum bereikt' })}
              </span>
            )}
          </div>
          <div className="mt-1 h-1.5 w-full rounded-full bg-gray-200 overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
            <div className={`h-full rounded-full ${p.plafond_bereikt ? 'bg-red-500' : 'bg-[#0B1F3A]'}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={detailId}
        className="mt-3 w-full flex items-center justify-center gap-1 text-sm text-[#0B1F3A] font-medium py-1"
      >
        {open ? t('professionals.minder', { defaultValue: 'Minder tonen' }) : t('professionals.meer', { defaultValue: 'Meer over {{naam}}', naam: p.naam.split(' ')[0] })}
        {open ? <ChevronUp className="w-4 h-4" aria-hidden="true" /> : <ChevronDown className="w-4 h-4" aria-hidden="true" />}
      </button>

      {open && (
        <div id={detailId} className="mt-2 space-y-3 border-t border-gray-100 pt-3">
          {p.intro_video_url && (
            <div>
              <p className="text-xs font-medium text-gray-700 flex items-center gap-1 mb-1">
                <Video className="w-3.5 h-3.5" aria-hidden="true" />
                {t('professionals.introVideo', { defaultValue: 'Kennismakingsvideo' })}
              </p>
              <video controls preload="metadata" playsInline src={p.intro_video_url} className="w-full rounded-lg bg-black" />
            </div>
          )}
          {p.bio && <p className="text-sm text-gray-700 whitespace-pre-line">{p.bio}</p>}
          {p.specialisaties.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {p.specialisaties.map((s) => (
                <Chip key={s}>{s}</Chip>
              ))}
            </div>
          )}
        </div>
      )}

      {fout && (
        <p className="text-sm text-red-600 mt-3" role="alert">
          {fout}
        </p>
      )}

      {vraagOpen && !heeftLopend ? (
        <DemografieVraag bezig={bezig} onStart={(o) => onStart(o)} onAnnuleer={() => setVraagOpen(false)} />
      ) : (
        <Button size="lg" className="w-full mt-3" onClick={knopActie} disabled={geblokkeerd || bezig} loading={bezig}>
          {geblokkeerd ? t('professionals.maxBereikt', { defaultValue: 'Maximum bereikt' }) : knopTekst}
        </Button>
      )}
    </Card>
  );
};

/** Pijler 1: gesloten bedrijfspool met professionals, gegroepeerd per discipline. */
export const MedewerkerProfessionalsPage: React.FC = () => {
  const { t } = useTranslation('medewerker');
  const navigate = useNavigate();
  const [filter, setFilter] = useState<string | null>(null);
  // Doorlink vanuit Expert Inloop (?discipline=<professional_type>)
  const [searchParams] = useSearchParams();
  const disciplineParam = searchParams.get('discipline');
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());

  const poolQuery = useQuery({ queryKey: ['medewerker-professionals'], queryFn: getProfessionals });

  const startMutation = useMutation({
    mutationFn: ({ professionalId, demografie }: { professionalId: string; demografie?: DemografieOpgave | null }) => startTraject(professionalId, demografie),
    onSuccess: (res) => {
      if (res.soort === 'dossier') navigate(`/plannen/${res.booking_token}`);
      else navigate(`/medewerker/vp/${res.melding_id}`);
    },
  });

  const professionals = useMemo(() => poolQuery.data?.professionals ?? [], [poolQuery.data?.professionals]);

  // Eenmalig het domein uit de doorlink als filter zetten, zodra de pool geladen is
  const [paramToegepast, setParamToegepast] = useState(false);
  if (!paramToegepast && disciplineParam && professionals.length > 0) {
    setParamToegepast(true);
    const label = professionals.find((p) => p.professional_type === disciplineParam)?.discipline_label;
    if (label) setFilter(label);
  }

  const disciplines = useMemo(() => Array.from(new Set(professionals.map((p) => p.discipline_label))), [professionals]);

  const groepen = useMemo(() => {
    const map = new Map<string, PortaalProfessional[]>();
    for (const p of professionals) {
      if (filter && p.discipline_label !== filter) continue;
      if (!map.has(p.discipline_label)) map.set(p.discipline_label, []);
      map.get(p.discipline_label)!.push(p);
    }
    return Array.from(map.entries());
  }, [professionals, filter]);

  const toggle = (id: string) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (poolQuery.isLoading) {
    return <div className="py-16 text-center text-gray-500">…</div>;
  }

  if (poolQuery.isError) {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm text-red-600" role="alert">
          {foutMelding(poolQuery.error, t('professionals.laadfout', { defaultValue: 'De pool kon niet worden geladen.' }))}
        </p>
        <Button variant="outline" className="mt-4" onClick={() => poolQuery.refetch()}>
          {t('algemeen.opnieuw', { defaultValue: 'Opnieuw proberen' })}
        </Button>
      </Card>
    );
  }

  const niveau = poolQuery.data?.niveau;
  const arbo = poolQuery.data?.arbo_module ?? false;
  const chipClass = (actief: boolean) =>
    `shrink-0 rounded-full border px-3 py-1.5 text-sm transition ${
      actief ? 'bg-[#0B1F3A] text-white border-[#0B1F3A]' : 'bg-white text-gray-700 border-gray-300'
    }`;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('pijler.professionals', { defaultValue: 'Mijn Professionals' })}</h1>
        <p className="text-sm text-gray-600 mt-0.5">
          {t('professionals.subtitel', { defaultValue: 'Jouw gesloten bedrijfspool' })}
          {niveau && ` — ${t('dashboard.niveau', { defaultValue: 'Niveau {{niveau}}', niveau })}`}
          {arbo && `, + Arbo`}
        </p>
      </div>

      {professionals.length === 0 ? (
        <Card className="p-6 text-center">
          <p className="text-gray-700">{t('professionals.leeg', { defaultValue: 'Je werkgever heeft de pool nog niet ingericht.' })}</p>
        </Card>
      ) : (
        <>
          {disciplines.length > 1 && (
            <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1" role="group" aria-label={t('professionals.filter', { defaultValue: 'Filter op discipline' })}>
              <button type="button" onClick={() => setFilter(null)} className={chipClass(filter === null)} aria-pressed={filter === null}>
                {t('professionals.alle', { defaultValue: 'Alle' })}
              </button>
              {disciplines.map((d) => (
                <button key={d} type="button" onClick={() => setFilter(d)} className={chipClass(filter === d)} aria-pressed={filter === d}>
                  {d}
                </button>
              ))}
            </div>
          )}

          {groepen.map(([discipline, lijst]) => (
            <section key={discipline}>
              <h2 className="text-sm font-semibold text-gray-900 mb-2">
                {discipline} <span className="text-gray-400 font-normal">({lijst.length})</span>
              </h2>
              <div className="space-y-3">
                {lijst.map((p) => {
                  const bezig = startMutation.isPending && startMutation.variables?.professionalId === p.professional_id;
                  const fout =
                    startMutation.isError && startMutation.variables?.professionalId === p.professional_id
                      ? foutMelding(startMutation.error, t('professionals.startMislukt', { defaultValue: 'Starten is niet gelukt. Probeer het opnieuw.' }))
                      : null;
                  return (
                    <ProfessionalKaart
                      key={p.professional_id}
                      p={p}
                      open={openIds.has(p.professional_id)}
                      onToggle={() => toggle(p.professional_id)}
                      bezig={bezig}
                      fout={fout}
                      onStart={(demografie) => startMutation.mutate({ professionalId: p.professional_id, demografie })}
                    />
                  );
                })}
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
};

export default MedewerkerProfessionalsPage;
